import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useUser } from 'src/features/authorization';
import useOneWayIn from '../../hooks/use-one-way-in';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import useToggle from 'src/hooks/use-toggle';

import { useTranslation } from 'react-i18next';
import { OneWayInStatus } from 'src/api/v2';
import { ONE_WAY_IN } from '../../routes';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import createOwiUpdateDto from '../utils/createOwiUpdateDto';
import { PageParams } from '../types/PageParams';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import { Alert, Button, Textarea } from '@innovationnorway/cl-react';

const Archiving = () => {
  const { t: tCommon } = useTranslation();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { formId } = useParams<PageParams>();
  const { oneWayInOpportunityById, updateOneWayInOpportunityStatus } = useOneWayIn(formId);
  const { isActive, activate, deactivate } = useToggle(false);
  const { isAadUser, user, displayName } = useUser();
  const siteimprove = useSiteimprove();

  const [comment, setComment] = useState('');
  const [error, setError] = useState(false);

  const opportunity = useMemo(() => {
    if (!oneWayInOpportunityById.data) return undefined;

    return oneWayInOpportunityById.data;
  }, [oneWayInOpportunityById.data]);

  const onError = () => {
    setError(true);
  };

  const onArchive = () => {
    const promise = updateOneWayInOpportunityStatus.mutateAsync(
      createOwiUpdateDto(
        opportunity?.id,
        OneWayInStatus.Closed,
        comment,
        isAadUser ? undefined : user?.clusterUserId,
        displayName || undefined,
      ),
    );
    promise.then(deactivate).catch(onError);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_ARCHIVING.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_ARCHIVING.actions.OLD,
    );
  };

  if (oneWayInOpportunityById.isFetching) {
    return (
      <div className="width--50">
        <LoadingPage />
      </div>
    );
  }

  if (opportunity?.isClosed) {
    return <Navigate to={ONE_WAY_IN.DETAILS.REPOPENING(formId as string)} />;
  }

  return (
    <>
      {error && (
        <Alert closeButton onClose={() => setError(false)} data-color="danger">
          {tOneWayIn('details.Archiving.Error')}
        </Alert>
      )}
      <h2 className="margin-bottom--3">{tOneWayIn('details.Archiving.Header')}</h2>
      <p>{`${tOneWayIn('details.Archiving.Ingress')}. ${tOneWayIn('details.Archiving.Ingress2')}`}</p>

      <Button onClick={activate}>{tOneWayIn('details.Archiving.Archive')}</Button>

      {!error && (
        <DialogContainer
          className="one-way-in-details__modal stack"
          title={tOneWayIn('details.Archiving.Modal.Header')}
          show={isActive}
          onClose={deactivate}
          titleHeadingLevel={2}
        >
          <p>{tOneWayIn('details.Archiving.Ingress2')}</p>
          <div>
            <label>{`${tCommon('note')} *`}</label>
            <Textarea onChange={(e) => setComment(e.target.value)} aria-required="true" maxLength={1000} />
          </div>
          <div className="display--flex gap--2">
            <Button disabled={comment.trim() === ''} onClick={onArchive}>
              {tOneWayIn('details.Archiving.Archive')}
            </Button>
            <Button variant="secondary" onClick={deactivate}>
              {tOneWayIn('details.Archiving.DoNotArchive')}
            </Button>
          </div>
        </DialogContainer>
      )}
    </>
  );
};

export default Archiving;
